import React from 'react'
import { Form } from 'react-bootstrap'
import { formatDateYYYYMMDD } from '../../../common/dateUtils'
import { IntegrationServiceId } from '../../../common/integrations'
import { SelectDropdown, SelectOption } from '../../SelectDropdown/SelectDropdown'
import { MultiSelectDropdown } from '../../MultiSelectDropdown/MultiSelectDropdown'
import { IntegrationFieldValue, IntegrationReviewField } from './IntegrationFieldReview'
import { IntegrationReferenceFieldSelect } from './IntegrationReferenceFieldSelect'
import { hasFieldValue } from './utils'

const MAX_VISIBLE_OPTIONS = 100

export interface FieldInputProps {
  field: IntegrationReviewField
  value: IntegrationFieldValue
  onEditValue: (apiName: string, value: IntegrationFieldValue) => void
  serviceId: IntegrationServiceId
  buildEntityRoutingKey: (kind: 'list' | 'single', referenceObjectName: string) => string
}

export const FieldInput: React.FC<FieldInputProps> = ({ field, value, onEditValue, serviceId, buildEntityRoutingKey }) => {
  const displayValue = hasFieldValue(value) ? (field.type === 'date' ? formatDateYYYYMMDD(String(value)) : String(value)) : ''

  if (field.type === 'boolean') {
    return (
      <div className='form-control border-0'>
        <Form.Check
          className='d-flex contact-information-checkbox'
          type='checkbox'
          aria-label={field.label}
          checked={value === true}
          onChange={(e) => onEditValue(field.apiName, e.target.checked)}
        />
      </div>
    )
  }

  if (field.type === 'multipicklist') {
    return (
      <MultiSelectDropdown
        name={field.apiName}
        className='mw-100'
        options={(field.options ?? []).map((o) => ({ id: o, label: o }))}
        initialSelectedOptions={Array.isArray(value) ? value : []}
        onSelectedChanged={(selected) => onEditValue(field.apiName, selected)}
        changeOnClose={false}
        searchable
        maxVisibleOptions={MAX_VISIBLE_OPTIONS}
        showRemovableChips
        placeholder='Not set'
      />
    )
  }

  if (field.type === 'reference') {
    return (
      <IntegrationReferenceFieldSelect
        serviceId={serviceId}
        referenceObjectName={field.referenceObjectName}
        value={value}
        onSelect={(id) => onEditValue(field.apiName, id)}
        buildEntityListRoutingKey={(name) => buildEntityRoutingKey('list', name)}
        buildEntitySingleRoutingKey={(name) => buildEntityRoutingKey('single', name)}
      />
    )
  }

  if (field.options?.length) {
    const options: SelectOption[] = field.options.map((o) => ({ id: o, label: o }))
    return (
      <SelectDropdown
        placeholder='Not set'
        options={options}
        initialSelectedOption={options.find((o) => o.id === displayValue) ?? null}
        onOptionSelected={(o) => onEditValue(field.apiName, o?.id ?? '')}
        className='w-100'
      />
    )
  }

  if (field.type === 'textarea') {
    return (
      <Form.Control
        as='textarea'
        rows={2}
        className='textarea-input-no-resize'
        placeholder='Not set'
        title={displayValue || undefined}
        value={displayValue}
        onChange={(e) => onEditValue(field.apiName, e.target.value)}
      />
    )
  }

  if (field.type === 'text') {
    return (
      <Form.Control
        as='textarea'
        rows={1}
        wrap='off'
        className='single-line-scroll-input'
        placeholder='Not set'
        value={displayValue}
        onChange={(e) => onEditValue(field.apiName, e.target.value)}
      />
    )
  }

  return (
    <Form.Control
      type={field.type === 'number' ? 'number' : 'date'}
      placeholder='Not set'
      value={displayValue}
      onChange={(e) => onEditValue(field.apiName, field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
    />
  )
}
